import { useEffect, useMemo, useState } from 'react';
import { apiClient } from '../api';
import { useAnalytics } from '../hooks/useAnalytics';
import { useWindowDimensions } from '../hooks/useWindowDimensions';
import { clientLogger } from '../utils/client-logger';
import { SurveyCompleteView } from './post-connect-survey/SurveyCompleteView';
import { SurveyQuestionInput } from './post-connect-survey/SurveyQuestionInput';
import type { SurveyAnswer, SurveyQuestion } from './post-connect-survey/types';
import {
  canProceedFromQuestion,
  getInitialAnswers,
  getNextVisibleIndex,
  serializeAnswers,
} from './post-connect-survey/surveyUtils';

const FALLBACK_SURVEY_VERSION = 'tap-connect-post-connect-v1';

export function PostConnectSurveyPage({
  surveyId,
  onBack,
  debugRedirectUrl,
}: {
  surveyId: string;
  onBack?: () => void;
  debugRedirectUrl?: string | null;
}) {
  const { track, page } = useAnalytics();
  const [surveyVersion, setSurveyVersion] = useState(FALLBACK_SURVEY_VERSION);
  const [questions, setQuestions] = useState<SurveyQuestion[]>([]);
  const [answers, setAnswers] = useState<SurveyAnswer[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const windowDimensions = useWindowDimensions(showConfetti);

  useEffect(() => {
    page('post_connect_survey');
    track('Post Connect Survey Viewed', { surveyVersion });
  }, [page, surveyVersion, track]);

  useEffect(() => {
    let isActive = true;
    apiClient
      .getPostConnectSurveyQuestions(surveyId)
      .then((data) => {
        if (!isActive) return;
        setSurveyVersion(data.surveyVersion);
        setQuestions(data.questions);
        setAnswers(getInitialAnswers(data.questions));
      })
      .catch((loadError) => {
        clientLogger.info('Post connect survey questions load failed', {
          error: loadError,
        });
        if (isActive)
          setError('We could not load the survey. Please try again.');
      })
      .finally(() => {
        if (isActive) setIsLoadingQuestions(false);
      });
    return () => {
      isActive = false;
    };
  }, [surveyId]);

  useEffect(() => {
    if (!isComplete) return;
    setShowConfetti(true);
    const confettiTimer = window.setTimeout(() => setShowConfetti(false), 4500);
    const redirectTimer = window.setTimeout(() => {
      if (redirectUrl) {
        window.location.href = redirectUrl;
      } else {
        window.location.replace('/dashboard');
      }
    }, 1800);
    return () => {
      window.clearTimeout(confettiTimer);
      window.clearTimeout(redirectTimer);
    };
  }, [isComplete, redirectUrl]);

  const question = questions[currentIndex];
  const nextIndex = getNextVisibleIndex(questions, currentIndex, 1, answers);
  const progress =
    questions.length > 0 ? ((currentIndex + 1) / questions.length) * 100 : 0;
  const canProceed = useMemo(
    () =>
      Boolean(question) &&
      canProceedFromQuestion(question, answers[currentIndex]),
    [answers, currentIndex, question]
  );

  const updateAnswer = (answer: SurveyAnswer) => {
    setAnswers((current) => {
      const next = [...current];
      next[currentIndex] = answer;
      return next;
    });
    setError(null);
  };

  const handleBack = () => {
    const previousIndex = getNextVisibleIndex(
      questions,
      currentIndex,
      -1,
      answers
    );
    if (previousIndex >= 0) {
      setCurrentIndex(previousIndex);
    } else {
      onBack?.();
    }
  };

  const submitSurvey = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setError(null);

    try {
      if (debugRedirectUrl !== undefined) {
        track('Post Connect Survey Submitted', {
          surveyVersion,
          debug: true,
        });
        track('Completion Redirect Started', {
          surveyVersion,
          hasRedirectUrl: Boolean(debugRedirectUrl),
          debug: true,
        });
        setRedirectUrl(debugRedirectUrl);
        setIsComplete(true);
        return;
      }

      const result = await apiClient.submitPostConnectSurveyResponse({
        surveyVersion,
        response: serializeAnswers(questions, answers),
        submittedAtClient: new Date().toISOString(),
      });
      track('Post Connect Survey Submitted', { surveyVersion });
      track('Completion Redirect Started', {
        surveyVersion,
        hasRedirectUrl: Boolean(result.redirectUrl),
      });
      setRedirectUrl(result.redirectUrl);
      setIsComplete(true);
    } catch (submitError) {
      clientLogger.info('Post connect survey submit failed', {
        error: submitError,
      });
      setError('We could not save your answers. Please try again.');
      track('Post Connect Survey Failed', {
        surveyVersion,
        error:
          submitError instanceof Error ? submitError.message : 'Unknown error',
      });
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    if (!canProceed || isSubmitting) return;
    if (nextIndex < questions.length) {
      setCurrentIndex(nextIndex);
      window.scrollTo(0, 0);
    } else {
      void submitSurvey();
    }
  };

  if (isComplete) {
    return (
      <SurveyCompleteView
        showConfetti={showConfetti}
        windowDimensions={windowDimensions}
        questionCount={questions.length}
      />
    );
  }

  if (error && questions.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center px-5 py-8 text-center">
        <div className="max-w-[360px]">
          <h1 className="font-display text-2xl font-bold text-ink mb-3">
            Survey unavailable
          </h1>
          <p className="text-sm text-ink-muted mb-6">{error}</p>
          <button
            type="button"
            data-testid="survey-unavailable-retry-button"
            onClick={() => window.location.reload()}
            className="rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-white hover:bg-primary-deep"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (isLoadingQuestions || !question) {
    return (
      <div className="flex-1 flex items-center justify-center px-5 py-8">
        <div className="h-8 w-8 rounded-full border-2 border-rule border-t-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex-1 flex items-center justify-center px-5 py-8">
      <div className="w-full max-w-[560px]">
        <div className="mb-3 flex items-center justify-between font-mono text-[11px] font-medium uppercase tracking-[0.1em] text-ink-faint">
          <span>
            Question {currentIndex + 1} of {questions.length}
          </span>
          <span className="text-primary">{question.section}</span>
        </div>
        <div className="mb-8 h-1 overflow-hidden rounded-full bg-rule-light">
          <div
            className="h-full rounded-full bg-primary transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>

        <h2 className="font-display text-[clamp(20px,5vw,24px)] font-bold leading-[1.3] tracking-[-0.02em] text-ink mb-6">
          {question.prompt}
          {question.type === 'text' && question.optional && (
            <span className="ml-2 inline-block align-middle rounded-full border border-rule px-2 py-1 font-mono text-[10px] font-medium uppercase tracking-[0.1em] text-ink-faint">
              Optional
            </span>
          )}
        </h2>
        {'helper' in question && question.helper && (
          <p className="-mt-4 mb-6 text-sm leading-[1.5] text-ink-muted">
            {question.helper}
          </p>
        )}

        <SurveyQuestionInput
          question={question}
          answer={answers[currentIndex]}
          onChange={updateAnswer}
        />

        {error && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
            {error}
          </div>
        )}

        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            data-testid="survey-back-button"
            onClick={handleBack}
            className="inline-flex items-center gap-2 px-0 py-2 text-sm font-medium text-ink-faint hover:text-ink-muted"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back
          </button>
          <button
            type="button"
            data-testid="survey-next-button"
            onClick={handleNext}
            disabled={!canProceed || isSubmitting}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-primary-deep disabled:cursor-not-allowed disabled:opacity-50"
          >
            {nextIndex >= questions.length
              ? isSubmitting
                ? 'Saving…'
                : 'Finish'
              : 'Next'}
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
